// 261029 P1/S4 — mỗi bản clip nói rõ đã gen bằng gì (thuần: không antd/router, jest chạy được). Đọc `clip.meta` BE ghi cho mọi lượt gen
// (`videoEngine`, `model` = ID API Seedance, `resolution`, `durationS`) — KHÔNG suy từ cấu hình hiện tại của video; trường thiếu ⇒ «không rõ». KHÔNG hiện giá.
import type { FilmEvent } from '../../types/film';
import { clipUsableFor } from './workflowActionPolicy';
import { clipCardsOf } from './videoStepRowUtils';

const UNKNOWN = 'không rõ';

/** BẢN SAO bảng `SEEDANCE_MODELS` của BE (`api/seedance/seedanceClient.ts`) ID API → nhãn; thêm các nhãn ngắn cũ. Id lạ ⇒ hiện nguyên id (không đoán). */
const MODEL_LABELS: Record<string, string> = {
  'dreamina-seedance-2-0-mini-260615': 'Seedance 2.0 mini',
  'dreamina-seedance-2-0-260128': 'Seedance 2.0 pro',
  'dreamina-seedance-2-0-fast-260128': 'Seedance 2.0 fast',
  'dreamina-seedance-2-5': 'Seedance 2.5',
  mini: 'Seedance 2.0 mini', pro: 'Seedance 2.0 pro', fast: 'Seedance 2.0 fast', '2.5': 'Seedance 2.5',
};

export function modelLabelOfApiId(id: unknown): string | null {
  if (typeof id !== 'string' || !id.trim()) return null;
  return MODEL_LABELS[id] ?? id;
}

export interface ClipGenInfo {
  /** `Seedance 2.0 mini · 480p · 10s` · `tải lên` · trường thiếu ⇒ «không rõ». */
  line: string;
  kind: 'GENERATED' | 'UPLOAD';
  /** `khác cấu hình hiện tại (2.0 · 10s)` khi bản không dùng được với cấu hình hiện tại của video; ngược lại null. */
  mismatch: string | null;
}

const secondsText = (n: unknown): string => (typeof n === 'number' && Number.isFinite(n) ? `${n}s` : UNKNOWN);
const metaOf = (clip: { meta?: unknown }): Record<string, unknown> => (clip.meta && typeof clip.meta === 'object' ? (clip.meta as Record<string, unknown>) : {});
/** Nhãn model + độ phân giải ĐỌC TỪ clip.meta (model thiếu ⇒ engine CỦA CLIP; không có ⇒ null — không suy từ video). */
const modelAndResolutionOf = (meta: Record<string, unknown>): { model: string | null; resolution: string | null } => {
  const engineLabel = meta.videoEngine === 'SEEDANCE_25' ? 'Seedance 2.5' : meta.videoEngine === 'SEEDANCE_20' ? 'Seedance 2.0' : null;
  return { model: modelLabelOfApiId(meta.model) ?? engineLabel, resolution: typeof meta.resolution === 'string' && meta.resolution ? meta.resolution : null };
};
const mismatchOf = (clip: { durationS?: number | null; meta?: unknown }, ev: { videoEngine?: string | null; genDurationS?: number | null }): string | null => {
  if (!ev.videoEngine || clipUsableFor(ev, clip)) return null;
  const cur = `${ev.videoEngine === 'SEEDANCE_25' ? '2.5' : '2.0'}${typeof ev.genDurationS === 'number' ? ` · ${ev.genDurationS}s` : ''}`;
  return `khác cấu hình hiện tại (${cur})`;
};

export function clipGenInfoOf(
  clip: { durationS?: number | null; meta?: unknown },
  ev: { videoEngine?: string | null; genDurationS?: number | null },
): ClipGenInfo {
  const meta = metaOf(clip);
  if (meta.provenance === 'UPLOAD') return { line: 'tải lên', kind: 'UPLOAD', mismatch: null };   // bản tải lên không do engine gen ⇒ không có cấu hình để so
  const { model, resolution } = modelAndResolutionOf(meta);
  const seconds = secondsText(typeof meta.durationS === 'number' ? meta.durationS : clip.durationS);
  return { line: `${model ?? UNKNOWN} · ${resolution ?? UNKNOWN} · ${seconds}`, kind: 'GENERATED', mismatch: mismatchOf(clip, ev) };
}

export interface ClipGenShort extends ClipGenInfo {
  /** Dòng ngắn cho ô của dải bản: `2.0 mini · 480p` · `2.5 · 720p` · `tải lên` · `không rõ`. */
  short: string;
  /** Dòng đầy đủ (= `clipGenInfoOf().line`) — dùng làm `title`. */
  full: string;
}

/** 261029 Rev 3 — dòng ngắn cho ô `VersionStrip`; bỏ tiền tố «Seedance » và giây (giây đã có ở nhãn ô). */
export function clipGenShortOf(
  clip: { durationS?: number | null; meta?: unknown },
  ev: { videoEngine?: string | null; genDurationS?: number | null },
): ClipGenShort {
  const info = clipGenInfoOf(clip, ev);
  if (info.kind === 'UPLOAD') return { ...info, short: 'tải lên', full: 'tải lên' };
  const { model, resolution } = modelAndResolutionOf(metaOf(clip));
  const short = !model && !resolution ? UNKNOWN : `${model ? model.replace(/^Seedance /, '') : UNKNOWN} · ${resolution ?? UNKNOWN}`;
  return { ...info, short, full: info.line };
}

/**
 * 261029 Rev 3 — bản đang PHÁT ở màn chính, đúng thứ tự `playUrl` của `WorkflowPromptVideoStep`: bản đang focus (phát được) → bản đang chọn (phát được) → bản phát được đầu tiên của dải.
 * Không có bản nào phát được ⇒ null (không hiện khối thông tin). Trả nguyên clip (meta + prompt).
 */
export function playingClipOf<T extends { id: string }>(ev: { clips?: T[] | null; selectedClipId?: string | null } | null | undefined, focusId: string | null): T | null {
  if (!ev) return null;
  const cards = clipCardsOf(ev as FilmEvent);
  const pick = (focusId && cards.find((c) => c.id === focusId && c.url)) || cards.find((c) => c.selected && c.url) || cards.find((c) => c.canPlay) || null;
  return pick ? ((ev.clips ?? []).find((c) => c.id === pick.id) ?? null) : null;
}
